import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutoResizeTextarea } from "@/components/ui/auto-resize-textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useAdditionalOfferReferenceForm, useCreatePortalForm, useOfferReferenceForm, usePartnerData, usePortalForms, useUpdatePortalForm } from "@/hooks/useLocalStorage";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { AlertDialog } from "@/widgets/AlertDailog";

export type CreateFormValues = {
    FormPartnerType: string;
    FormName: string;
    ExistingFormId?: number;
};

type CreateFormDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialData?: any;
    fromType: string
    offerData?: any | null;
};

export function CreateFormDialog({ open, onOpenChange, initialData, fromType, offerData }: CreateFormDialogProps) {
    const [documentValidationError, setDocumentValidationError] = useState<string>("");
    const createPortalForm = useCreatePortalForm();
    const offerReferenceForm = useOfferReferenceForm();
    const additionalOfferReferenceForm = useAdditionalOfferReferenceForm();
    const updatePortalForm = useUpdatePortalForm();
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);
    const isPartnerRequired = fromType === "PartnerForm";
    const isDefaultOffer = fromType === "DefaultOffer" || fromType === "AdditionalOffer";
    console.log("offerData121", offerData)
    const { data: existingForms, isLoading } = usePortalForms(
        1,
        undefined,
        fromType === "SubForm" ? "sf" : fromType
    );

    const { data: partners } = usePartnerData(
        1,
        undefined,
    );
    console.log("partners", partners)
    const form = useForm<CreateFormValues>({
        defaultValues: {
            FormPartnerType: "",
            FormName: "",
            ExistingFormId: undefined,
        },
    });

    const { control, register, handleSubmit, reset, setValue, watch, getValues } = form;

    useEffect(() => {
        if (open && initialData) {
            console.log("Initial Data:", initialData);
            console.log("FormPartnerType:", initialData.FormPartnerType);
            console.log("Available Partners:", partners?.data?.items);

            const partnerTypeValue = initialData.FormPartnerType
                ? initialData.FormPartnerType.toString()
                : "";

            reset({
                FormName: initialData.FormName || "",
                FormPartnerType: partnerTypeValue,
            });
        } else if (open && !initialData) {
            reset({
                FormPartnerType: "",
                FormName: offerData?.ExchangeOfferName ?? "",
                ExistingFormId: undefined,
            });
        }
        setDocumentValidationError("");
    }, [open, initialData, reset, partners, offerData]);

    const onSubmit = async (data: CreateFormValues) => {
        if (initialData) {
            updatePortalForm.mutateAsync({
                Id: initialData.FormId,
                data,
                formType: fromType === "SubForm" ? "sf" : fromType
            });
        } else {
            const response = await createPortalForm.mutateAsync({ data, formType: fromType === "SubForm" ? "sf" : fromType });
            console.log("response909", response)
            if (fromType === "DefaultOffer") {
                await offerReferenceForm.mutateAsync({
                    Id: offerData.Id,
                    referenceFormId: response.Id
                });
            }
            if (fromType === "AdditionalOffer") {
                await additionalOfferReferenceForm.mutateAsync({
                    Id: offerData.ExPartnerId,
                    additionalOfferId: offerData.Id,
                    referenceFormId: response.Id
                });
            }
        }
    };

    useEffect(() => {
        if (createPortalForm.isSuccess === true) {
            setReqStatus(createPortalForm.isSuccess);
            setAlertMessage(`Form Created Successfully!`);
            setAlertOpen(true);
            onOpenChange(false);
            createPortalForm.reset();
        } else if (createPortalForm.isError) {
            const raw = createPortalForm.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(createPortalForm.isSuccess);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            onOpenChange(false);
            createPortalForm.reset();
        }
    }, [createPortalForm]);

    useEffect(() => {
        if (updatePortalForm.isSuccess === true) {
            setReqStatus(updatePortalForm.isSuccess);
            setAlertMessage(`Form Updated Successfully!`);
            setAlertOpen(true);
            onOpenChange(false);
            updatePortalForm.reset();
        } else if (updatePortalForm.isError) {
            const raw = updatePortalForm.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(updatePortalForm.isSuccess);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            onOpenChange(false);
            updatePortalForm.reset();
        }
    }, [updatePortalForm]);

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {initialData ? "Edit Form" : "Create Form"}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">

                            {!initialData && !isDefaultOffer && (
                                <div className="space-y-2">
                                    <Label htmlFor="ExistingFormId">Existing Form (Optional)</Label>
                                    <Controller
                                        name="ExistingFormId"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value?.toString()}
                                                onValueChange={(val) => field.onChange(Number(val))}
                                            >
                                                <SelectTrigger id="ExistingFormId">
                                                    <SelectValue placeholder="Select Existing Form" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {existingForms?.items?.map((form: any) => (
                                                        <SelectItem key={form.FormId} value={form.FormId.toString()}>
                                                            {form.FormName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            )}

                            {!isDefaultOffer && (
                                <div className="space-y-1">
                                    <Label htmlFor="FormPartnerType">Partner Type {isPartnerRequired && <span className="text-red-500">*</span>}</Label>
                                    <Controller
                                        name="FormPartnerType"
                                        control={control}
                                        rules={
                                            isPartnerRequired
                                                ? { required: "Partner Type is required" }
                                                : {}
                                        }
                                        render={({ field }) => (
                                            <Select
                                                value={field.value}
                                                onValueChange={(value) => field.onChange(value)}
                                            >
                                                <SelectTrigger id="FormPartnerType">
                                                    <SelectValue placeholder="Select Partner Type" />
                                                </SelectTrigger>

                                                <SelectContent>
                                                    {partners?.data?.items?.map((partner: any) => (
                                                        <SelectItem
                                                            key={partner.ID}
                                                            value={partner.ID.toString()}
                                                        >
                                                            {partner.PartnerName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {form.formState.errors.FormPartnerType && (
                                        <p className="text-red-500 text-xs">
                                            {form.formState.errors.FormPartnerType.message}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="FormName">Form Title <span className="text-red-500">*</span></Label>
                                <AutoResizeTextarea
                                    id="FormName"
                                    placeholder="Form Title"
                                    maxLength={100}
                                    {...register("FormName", { required: "Form Title is required" })}
                                    value={form.watch("FormName") || ""}
                                />
                                {form.formState.errors.FormName && (
                                    <p className="text-red-500 text-xs">
                                        {form.formState.errors.FormName.message}
                                    </p>
                                )}
                            </div>

                        </div>

                        <div className="flex justify-end space-x-2">
                            <Button type="submit">
                                {initialData ?
                                    updatePortalForm.isPending ?
                                        (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                Updating...
                                            </>
                                        ) : (
                                            "Update"
                                        ) :
                                    createPortalForm.isPending ?
                                        (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            "Save"
                                        )}
                            </Button>
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </>
    );
}